import { connectRoutes, Options, StateGetter, redirect } from 'redux-first-router';
import createRestoreScroll from 'redux-first-router-restore-scroll';
import get from 'lodash/get';
import querySerializer from 'qs';

import routeMap, { RouteHook } from 'app/routes';
import * as selectors from './selectors';
import * as actions from './actions';
import { Dispatch } from 'redux';
import { getComponent, fetchComponent } from './component-loader';

import { setUserData } from 'dux/user/actions';

import { ROUTER_INITIALIZE, ERROR } from './types';
import { getRouterIsInitialized } from './selectors';
import { initializeEnvData } from './initializers';

const NOOP = () => undefined;

type HookType = 'beforeEnter'|'afterEnter';
const getHook = (type: HookType, routeType: string) => get(routeMap, `${routeType}.${type}`, NOOP ) as RouteHook;

async function getRouteComponent( routeType: any, dispatch: Dispatch, getState: StateGetter ) {
    let Component = getComponent( routeType );
    if ( !Component ) {
        Component = await fetchComponent( routeType, get( routeMap, `${routeType}.getComponent`, NOOP as any ) );
    }

    // Need to check here because browser caching will sometimes return a component
    //  even though the store isn't aware of it
    if ( !selectors.getComponentIsLoaded( getState(), routeType ) ) {
        dispatch( actions.componentIsLoaded( routeType ) );
    }
    return Component;
}

export const router = connectRoutes( routeMap, {
    querySerializer,
    restoreScroll: createRestoreScroll({ manual: true }),
    initialDispatch: false,
    onBeforeChange: ( dispatch, getState, bag ) => {
        const routeType = get( bag, 'action.type', '' );
        getHook('beforeEnter', routeType)( dispatch, getState, bag );
    },
    onAfterChange: async ( dispatch, getState, bag ) => {
        const routeType = selectors.getCurrentRouteType( getState() );
        const loadingTimeoutId = setTimeout( () => {
            dispatch( actions.routeIsLoading() );
        }, 100 );

        await Promise.all( [
            getRouteComponent( routeType, dispatch, getState ),
            getHook('afterEnter', routeType)( dispatch, getState, bag ),
        ] );

        clearTimeout( loadingTimeoutId );

        // in case a redirect has happened in the `afterEnter` async hook or user action,
        // do not complete the route transition
        if ( selectors.getCurrentRouteType( getState() ) === routeType ) {
            dispatch( actions.routeLoaded( routeType ) );
            dispatch( actions.restoreScroll() );
        }
    },

} as Options);

export const initializeRouter = ( userData: any ) => async ( dispatch: Dispatch, getState: StateGetter ) => {
    dispatch( setUserData( userData ) );

    if ( getRouterIsInitialized( getState() ) ) {
        return;
    } else {
        dispatch( { type: ROUTER_INITIALIZE } );
    }

    let hasInitError = false;
    try {
        await dispatch( initializeEnvData as any );
    } catch ( error ) {
        console.error( error );
        hasInitError = true;
    }

    if (router.initialDispatch) {
        await router.initialDispatch();
    }

    if ( hasInitError ) {
        return dispatch( redirect( { type: ERROR } ) );
    }

    const client = getState().client || {};
    if ( client.bundleList && Object.keys( client.bundleList ).length === 0 ) {
        return dispatch( redirect( { type: ERROR } ) );
    }

    await initializeEnvData( dispatch, getState );
};
